import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff, MapPin, DollarSign } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';
import { LOGO_URL } from '../data/mockData';
import { leadsAPI } from '../services/api';
import { trackRegistration } from '../utils/googleAds';

const PRICE_RANGES = [
  { value: 'Under $400K', label: 'Under $400,000' },
  { value: '$400K - $600K', label: '$400,000 - $600,000' },
  { value: '$600K - $800K', label: '$600,000 - $800,000' },
  { value: '$800K - $1M', label: '$800,000 - $1,000,000' },
  { value: '$1M - $1.5M', label: '$1,000,000 - $1,500,000' },
  { value: '$1.5M+', label: '$1,500,000+' },
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
    preferredArea: '',
    priceRange: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [consentChecked, setConsentChecked] = useState(false);
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    if (!formData.preferredArea.trim()) {
      setError('Please enter your preferred area');
      return;
    }

    if (!formData.priceRange) {
      setError('Please select a price range');
      return;
    }

    if (!consentChecked) {
      setError('Please agree to the Terms of Use and Privacy Policy to create an account');
      return;
    }

    setLoading(true);
    
    try {
      // Register the user with preferences
      await register(
        formData.name, 
        formData.email, 
        formData.password, 
        formData.phone,
        formData.preferredArea,
        formData.priceRange
      );
      
      // Track Google Ads conversion for registration
      trackRegistration();
      
      navigate('/');
    } catch (err) {
      setError('Failed to create account');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center py-12 px-4">
      <Card className="w-full max-w-md shadow-xl border-0">
        <CardHeader className="text-center pb-2">
          <Link to="/" className="inline-block mb-4">
            <img 
              src={LOGO_URL} 
              alt="Brantford Area Homes" 
              className="h-20 w-20 mx-auto object-cover rounded-full"
            />
          </Link>
          <CardTitle className="text-2xl font-bold text-gray-900">Create Account</CardTitle>
          <p className="text-gray-500 text-sm">Join BrantfordAreaHomes.ca to save properties</p>
        </CardHeader>
        <CardContent className="pt-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 text-sm rounded-lg">
                {error}
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Full Name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="John Smith"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  placeholder="you@example.com"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Phone</label>
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  placeholder="(226) 555-1234"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Preferred Area *</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type="text"
                  value={formData.preferredArea}
                  onChange={(e) => setFormData({...formData, preferredArea: e.target.value})}
                  placeholder="e.g. Brantford, Hamilton, Ancaster"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Price Range *</label>
              <div className="relative">
                <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 z-10" size={18} />
                <Select 
                  value={formData.priceRange || undefined} 
                  onValueChange={(v) => setFormData({...formData, priceRange: v})}
                  required
                >
                  <SelectTrigger className="pl-10 h-11">
                    <SelectValue placeholder="Select price range" />
                  </SelectTrigger>
                  <SelectContent>
                    {PRICE_RANGES.map(range => (
                      <SelectItem key={range.value} value={range.value}>{range.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  placeholder="••••••••"
                  className="pl-10 pr-10 h-11"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData({...formData, confirmPassword: e.target.value})}
                  placeholder="••••••••"
                  className="pl-10 h-11"
                  required
                />
              </div>
            </div>

            <div className="flex items-start gap-2 pt-1">
              <input
                data-testid="register-consent"
                id="register-consent"
                type="checkbox"
                checked={consentChecked}
                onChange={(e) => setConsentChecked(e.target.checked)}
                className="mt-1 h-4 w-4 rounded border-gray-300 text-red-600 focus:ring-red-500 cursor-pointer"
              />
              <label htmlFor="register-consent" className="text-xs text-gray-600 cursor-pointer leading-relaxed">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-red-600 hover:underline">Terms of Use</Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-red-600 hover:underline">Privacy Policy</Link>,
                and consent to receiving property listings and real estate updates from Alexander Conti.
              </label>
            </div>

            <Button 
              type="submit" 
              disabled={loading || !consentChecked}
              className="w-full h-11 bg-red-600 hover:bg-red-700 text-white disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-red-600 font-medium hover:text-red-700">
              Sign in
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default RegisterPage;
